import { z } from 'zod';

export const productFormSchema = z.object({
  // Basic required fields
  product_name: z.string().min(1, 'Product name is required'),
  slug: z
    .string()
    .min(1, 'Slug is required')
    .regex(/^[a-z0-9-]+$/, 'Invalid slug format'),
  product_type: z.enum(['digital', 'physical', 'services']),
  variant_mode: z.enum(['single', 'variant']),

  // Optional basic fields
  sku: z.string().optional(),
  status: z.number().optional(),

  // Digital product validation
  is_digital_download: z.boolean().optional(),
  digital_file_url: z
    .string()
    .url('Invalid URL format')
    .optional()
    .or(z.literal('')),
  download_limit: z
    .number()
    .min(1, 'Download limit must be at least 1')
    .max(9999, 'Download limit cannot exceed 9999')
    .optional()
    .nullable(),
  download_expiry_days: z
    .number()
    .min(1, 'Expiry days must be at least 1')
    .max(3650, 'Expiry days cannot exceed 10 years')
    .optional()
    .nullable(),
  activation_keys: z
    .string()
    .optional()
    .refine(val => {
      if (!val || !val.trim()) return true;
      const lines = val.split('\n').filter(line => line.trim());
      return lines.every(line => line.trim().length >= 8);
    }, 'All activation keys must be at least 8 characters'),

  // Physical product validation
  weight: z.number().min(0, 'Weight must be positive').optional().nullable(),
  length: z.number().min(0, 'Length must be positive').optional().nullable(),
  width: z.number().min(0, 'Width must be positive').optional().nullable(),
  height: z.number().min(0, 'Height must be positive').optional().nullable(),
  manufacturer: z.string().optional(),
  supplier_id: z.number().optional().nullable(),

  // Service product validation
  duration: z.string().optional(),
  service_type: z.string().optional(),
  min_quantity: z
    .number()
    .min(1, 'Minimum quantity must be at least 1')
    .optional()
    .nullable(),
  max_quantity: z
    .number()
    .min(1, 'Maximum quantity must be at least 1')
    .optional()
    .nullable(),

  // Common fields
  delivery_time: z.string().optional(),
  digital_delivery_time: z.string().optional(),
  delivery_instructions: z.string().optional(),
  shipping_cost: z
    .number()
    .min(0, 'Shipping cost must be positive')
    .optional()
    .nullable(),
  free_shipping_threshold: z
    .number()
    .min(0, 'Free shipping threshold must be positive')
    .optional()
    .nullable(),
  estimated_delivery_days: z
    .number()
    .min(1, 'Estimated delivery days must be at least 1')
    .optional()
    .nullable(),

  // Boolean fields
  requires_shipping_address: z.boolean().optional(),
  requires_consultation: z.boolean().optional(),
  is_customizable: z.boolean().optional(),
  track_inventory: z.boolean().optional(),
  allow_backorder: z.boolean().optional(),
  is_featured: z.boolean().optional(),
  is_active: z.boolean().optional(),
  track_digital_stock: z.boolean().optional(),

  // Arrays
  tags: z.array(z.any()).optional(),
  newTags: z.array(z.any()).optional(),
  categories: z.array(z.any()).optional(),
  media: z.array(z.any()).optional(),
});

export const validateProductForm = (data: any) => {
  const errors: string[] = [];

  try {
    productFormSchema.parse(data);
  } catch (error) {
    if (error instanceof z.ZodError) {
      error.errors.forEach(err => {
        errors.push(err.message);
      });
    }
  }

  // Additional business logic validation
  if (data.product_type === 'digital' && data.is_digital_download) {
    if (!data.digital_file_url && !data.primary_digital_file_url) {
      errors.push(
        'At least one digital file URL is required for digital downloads',
      );
    }

    if (data.requires_activation_key && !data.activation_keys?.trim()) {
      errors.push(
        'Activation keys are required when activation key is enabled',
      );
    }

    if (
      data.track_digital_stock &&
      (!data.digital_stock_quantity || data.digital_stock_quantity < 1)
    ) {
      errors.push(
        'Valid stock quantity is required when digital stock tracking is enabled',
      );
    }
  }

  if (data.product_type === 'physical') {
    if (data.requires_shipping_address && (!data.weight || data.weight <= 0)) {
      errors.push(
        'Weight is required for physical products that require shipping',
      );
    }
  }

  if (data.product_type === 'services') {
    if (
      data.min_quantity &&
      data.max_quantity &&
      data.min_quantity > data.max_quantity
    ) {
      errors.push('Minimum quantity cannot be greater than maximum quantity');
    }
  }

  return errors;
};

export default productFormSchema;
